import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Filter, Hash, ArrowRight, Flame, Heart, MessageCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import FashionCard from '../components/FashionCard';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANDS = ['All', 'Louis Vuitton', 'Prada', 'Gucci', 'Hermes', 'Chanel'];

const SORT_OPTIONS = [
  { key: 'trending', label: 'Trending', icon: Flame },
  { key: 'recent', label: 'Recent', icon: Clock },
  { key: 'most_liked', label: 'Most Liked', icon: Heart },
  { key: 'most_commented', label: 'Most Discussed', icon: MessageCircle },
];

const FeedPage = () => {
  const { isAuthenticated } = useAuth();
  const [looks, setLooks] = useState([]);
  const [brandWeek, setBrandWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [sortBy, setSortBy] = useState('trending');

  const fetchLooks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: sortBy };
      if (selectedBrand !== 'All') params.brand = selectedBrand;
      const [looksRes, bwRes] = await Promise.all([
        axios.get(`${API}/looks`, { params }),
        axios.get(`${API}/brand-week`)
      ]);
      setLooks(looksRes.data);
      if (bwRes.data.active) setBrandWeek(bwRes.data);
    } catch {
      toast.error('Failed to load looks');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, sortBy]);

  useEffect(() => {
    fetchLooks();
  }, [fetchLooks]);

  return (
    <div className="min-h-screen pt-20 bg-[#0a0a0a]" data-testid="feed-page">
      {/* Header */}
      <div className="py-12 md:py-16 px-6 md:px-12 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C41E3A] mb-4">Community</p>
              <h1 className="text-4xl md:text-5xl font-serif italic text-white">Fashion Feed</h1>
            </div>
            <div className="flex items-center gap-4">
              {/* Brand Filter */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2 border-white/20 text-white hover:bg-white/5" data-testid="filter-dropdown">
                    <Filter className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wider">{selectedBrand}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#1c1c1c] border-white/10">
                  {BRANDS.map((brand) => (
                    <DropdownMenuItem
                      key={brand}
                      onClick={() => setSelectedBrand(brand)}
                      className={selectedBrand === brand ? 'text-[#C41E3A]' : ''}
                      data-testid={`filter-${brand.toLowerCase().replace(/ /g, '-')}`}
                    >
                      {brand}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Create Button */}
              {isAuthenticated && (
                <Link to="/create" data-testid="create-look-btn-feed">
                  <Button className="gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white">
                    <Plus className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wider">Share Look</span>
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sort Tabs */}
      <div className="px-6 md:px-12 pt-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-1 p-1 bg-[#1c1c1c] rounded-sm border border-white/5 w-full sm:w-fit overflow-x-auto hide-scrollbar" data-testid="sort-tabs">
            {SORT_OPTIONS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setSortBy(key)}
                className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider font-medium transition-all rounded-sm ${
                  sortBy === key
                    ? 'bg-[#C41E3A] text-white'
                    : 'text-gray-500 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`sort-${key}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Brand Week Banner */}
      {brandWeek && (
        <div className="px-6 md:px-12 pt-6">
          <div className="max-w-7xl mx-auto">
            <Link to="/one-brand-week" className="block group" data-testid="feed-brand-week-banner">
              <div className="relative overflow-hidden border border-white/10 hover:border-white/25 transition-all duration-500 p-5 md:p-6" style={{ background: `linear-gradient(135deg, ${brandWeek.accent_color}10, #111111, ${brandWeek.accent_color}05)` }}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <span className="w-2 h-2 rounded-full animate-pulse shrink-0" style={{ backgroundColor: brandWeek.accent_color }} />
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: brandWeek.accent_color }}>One Brand Week</p>
                      <p className="text-white font-serif italic">{brandWeek.brand_name}: {brandWeek.tagline}</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 shrink-0 group-hover:translate-x-1 transition-transform" style={{ color: brandWeek.accent_color }} />
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* Campaign Banner */}
      <div className="px-6 md:px-12 pt-6">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/campaign/runway-to-real-life" 
            className="block group"
            data-testid="campaign-banner"
          >
            <div className="relative overflow-hidden rounded-sm border border-[#C41E3A]/20 bg-gradient-to-r from-[#C41E3A]/10 via-[#1c1c1c] to-[#C41E3A]/5 p-6 md:p-8 hover:border-[#C41E3A]/40 transition-all duration-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#C41E3A]/20 border border-[#C41E3A]/30 flex items-center justify-center shrink-0">
                    <Hash className="w-5 h-5 text-[#C41E3A]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-serif italic text-white mb-1">#RunwayToRealLife</h3>
                    <p className="text-sm text-gray-400">Join the campaign. Share how you bring runway fashion to your everyday style.</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[#C41E3A] group-hover:text-white transition-colors shrink-0">
                  <span className="text-xs uppercase tracking-wider font-medium">View Campaign</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Feed Grid */}
      <div className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <FeedContent 
            loading={loading} 
            looks={looks} 
            isAuthenticated={isAuthenticated} 
          />
        </div>
      </div>
    </div>
  );
};

// Extracted sub-components to reduce complexity
const FeedSkeleton = () => (
  <div className="bento-grid">
    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
      <div key={`skeleton-${num}`} className="skeleton rounded-sm" style={{minHeight: '350px'}} />
    ))}
  </div>
);

const FeedGrid = ({ looks }) => (
  <div className="bento-grid">
    {looks.map((look, index) => (
      <FashionCard 
        key={look.id} 
        look={look} 
        featured={index === 0 || index === 5}
      />
    ))}
  </div>
);

const EmptyFeed = ({ isAuthenticated }) => (
  <div className="text-center py-24">
    <p className="text-gray-500 text-lg mb-6">No fashion looks yet</p>
    {isAuthenticated ? (
      <Link to="/create">
        <Button className="btn-luxury btn-luxury-accent">
          Be the first to share
        </Button>
      </Link>
    ) : (
      <Link to="/auth">
        <Button className="btn-luxury btn-luxury-secondary">
          Sign in to share looks
        </Button>
      </Link>
    )}
  </div>
);

const FeedContent = ({ loading, looks, isAuthenticated }) => {
  if (loading) {
    return <FeedSkeleton />;
  }
  
  if (looks.length > 0) {
    return <FeedGrid looks={looks} />;
  }
  
  return <EmptyFeed isAuthenticated={isAuthenticated} />;
};

export default FeedPage;
